import type { JSX } from "react";
import React, { useMemo } from "react";
import * as Scrivito from "scrivito";
import type { TranslationsMap } from "stores/localsStore";
import { useLocalsStore } from "stores/localsStore";
import defaultTranslations from "config/locals/defaultTranslations";
import { TranslateWrapper } from "components/TranslateWrapper";

const TRANSLATE_TEMPLATE_REGEXP = /\{{2}\w+}{2}/;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Args = any[];
type Translate = (...args: Args) => string | JSX.Element;

const normalizeTranslation = (str: string) => {
  if (typeof str !== "string") return str;

  return str
    .replace(/^"(.*)"$/, "$1")
    .replace(/\\"/g, '"')
    .replace(/""/g, '"');
};

export const useTranslate = () => {
  const locals = useLocalsStore((state) => state.locals);
  const isEditMode = useMemo(() => Scrivito.isInPlaceEditingActive(), []);

  const memoTranslate = useMemo(() => {
    const translate: Translate = (
      key: string,
      replacers: string[] | null = null,
    ): React.JSX.Element | string => {
      // clean translation key
      const correctedKey = key.replace(/ |-/, ""); // if key name has inconsistent chars like "-" or " " replace them by "_"
      const upperKey = correctedKey.toUpperCase();

      const localsAttrName = getLocalsAttrName(upperKey);

      if (!localsAttrName) {
        return <>{key}</>;
      }

      const translations = locals[localsAttrName];
      const defaultTranslationsMap = defaultTranslations as TranslationsMap;

      let text =
        translations?.[upperKey] ||
        defaultTranslationsMap[upperKey] ||
        upperKey;

      if (replacers && replacers.length > 0) {
        text = insertReplacers(text, replacers);
      }

      if (text.startsWith("@@")) {
        text = text.replace(/^@@\s?/, "");
      }
      const normalized = normalizeTranslation(text);
      return isEditMode ? (
        <TranslateWrapper text={normalized} tooltipText={upperKey} />
      ) : (
        normalized
      );
    };

    return withStringOnly(translate);
  }, [locals, isEditMode]);

  return memoTranslate;
};

const translateFunction: Translate = (
  key: string,
  replacers: string[] | null = null,
): React.JSX.Element | string => {
  const { locals } = useLocalsStore.getState();
  const isEditMode = Scrivito.isInPlaceEditingActive();
  // clean translation key
  const correctedKey = key.replace(/ |-/, ""); // if key name has inconsistent chars like "-" or " " replace them by "_"
  const upperKey = correctedKey.toUpperCase();

  const localsAttrName = getLocalsAttrName(upperKey);

  if (!localsAttrName) {
    return <>{key}</>;
  }

  const translations = locals[localsAttrName];
  const defaultTranslationsMap = defaultTranslations as TranslationsMap;

  let text =
    translations?.[upperKey] || defaultTranslationsMap[upperKey] || upperKey;

  if (replacers && replacers.length > 0) {
    text = insertReplacers(text, replacers);
  }

  const normalized = normalizeTranslation(text);
  return isEditMode ? (
    <TranslateWrapper text={normalized} tooltipText={upperKey} />
  ) : (
    normalized
  );
};

const withStringOnly = (translateFn: Translate) => {
  const enhancedTranslate = (...args: Args) => translateFn(...args);

  enhancedTranslate.stringOnly = (...args: Args): string => {
    const result = translateFn(...args);

    return typeof result === "string" ? result : result.props.text;
  };

  return enhancedTranslate;
};

export const translate = withStringOnly(translateFunction);

const checkIfKeyValid = (key: string): boolean => {
  if (!/^[A-Z][_A-Z0-9]*$/.test(key)) {
    console.error(
      `key: ${key} needs to be type string and be written using capital letters, numbers and underscore separator, can start with a letter only i.e. DE_M3PH`,
    );
    return false;
  }
  return true;
};

const insertReplacers = (string: string, replacers: string[] = []) => {
  if (typeof replacers === "string") {
    return string.replace(TRANSLATE_TEMPLATE_REGEXP, replacers);
  }

  return replacers.reduce(
    (res, elem) => res.replace(TRANSLATE_TEMPLATE_REGEXP, elem),
    string,
  );
};

const getLocalsAttrName = (key: string): string | undefined => {
  if (!checkIfKeyValid(key)) {
    return undefined;
  }
  if (key[0] >= "A" && key[0] <= "E") {
    return "localsAE";
  }
  if (key[0] >= "F" && key[0] <= "J") {
    return "localsFJ";
  }
  if (key[0] >= "K" && key[0] <= "O") {
    return "localsKO";
  }
  if (key[0] >= "P" && key[0] <= "T") {
    return "localsPT";
  }
  return "localsUZ";
};
